import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL,
} from '@solana/web3.js'
import { getCachedTotalLp } from './lpTracker'

// Presale configuration and logic
export const PRESALE_CONFIG = {
  tokenName: '$PULSE',
  tokenSymbol: 'PULSE',
  totalSupply: 1000000000, // 1 billion tokens
  presaleSupply: 200000000, // 200M tokens (20% of total)
  airdropSupply: 500000000, // 500M tokens (50% of total)
  lpSupply: 200000000, // 200M tokens (20% of total)
  teamMarketingSupply: 100000000, // 100M tokens (10% of total)

  // Initial LP: $50,000 paired with 200M tokens (20% of supply)
  initialLpValue: 50000, // USD
  initialLpTokens: 200000000, // 200M tokens

  // Pricing calculation
  // Initial price = $50,000 / 200M = $0.00025 per token
  // Presale gets 40% discount, so presale price = $0.00025 * 0.6 = $0.00015 per token
  // Assuming SOL price of ~$150, presale price ≈ 0.000001 SOL per token
  pricePerToken: 0.00000074, // SOL per token (presale price with 40% discount)
  initialListingPrice: 0.00000167, // SOL per token (initial listing price)

  // Presale details
  presaleDurationHours: 48, // 48 hours only
  minPurchase: 0.1, // SOL
  maxPurchase: 50, // SOL

  // Launch date: Thursday, December 11th, 2025
  launchDate: new Date('2025-12-11T00:00:00Z'),

  // Presale starts Monday (calculate next Monday)
  getPresaleStartDate(): Date {
    const now = new Date()
    const monday = new Date(now)
    monday.setDate(now.getDate() + ((1 + 7 - now.getDay()) % 7 || 7))
    monday.setHours(0, 0, 0, 0)
    return monday
  },

  getPresaleEndDate(): Date {
    const start = this.getPresaleStartDate()
    const end = new Date(start)
    end.setHours(end.getHours() + this.presaleDurationHours)
    return end
  },

  // Hard cap: fixed 600 SOL
  hardCap: 600, // SOL

  getHardCap(): number {
    return this.hardCap
  },

  // Team initial LP: fixed 400 SOL
  teamInitialLp: 400, // SOL

  // LP wallet address for tracking additional LP
  lpWalletAddress: 'BhbAcy3LeREyhV2DGGJ9vrXCfrY25ywahWQbXbNKLhEk',

  // Purchase wallet address - SOL sent here for LP purchases
  purchaseWalletAddress: 'BhbAcy3LeREyhV2DGGJ9vrXCfrY25ywahWQbXbNKLhEk',
}

// Mock state (in real app, this would come from blockchain)
let mockRaised = 0 // SOL
let mockParticipants = 0

export function getPresaleStats() {
  const hardCap = PRESALE_CONFIG.getHardCap()
  return {
    raised: mockRaised,
    goal: hardCap,
    participants: mockParticipants,
    progress: hardCap > 0 ? (mockRaised / hardCap) * 100 : 0,
    tokensSold: Math.floor(mockRaised / PRESALE_CONFIG.pricePerToken),
    tokensRemaining:
      PRESALE_CONFIG.presaleSupply -
      Math.floor(mockRaised / PRESALE_CONFIG.pricePerToken),
    presaleSupply: PRESALE_CONFIG.presaleSupply,
    discount: 40, // 40% discount
    teamInitialLp: PRESALE_CONFIG.teamInitialLp,
  }
}

export function calculateTokensFromSol(solAmount: number): number {
  return Math.floor(solAmount / PRESALE_CONFIG.pricePerToken)
}

export function calculateSolFromTokens(tokenAmount: number): number {
  return tokenAmount * PRESALE_CONFIG.pricePerToken
}

/**
 * Real purchase function that sends SOL to the purchase wallet
 * @param solAmount Amount of SOL to send
 * @param publicKey User's public key
 * @param connection Solana connection
 * @param sendTransaction Wallet adapter sendTransaction function
 * @returns Transaction signature and tokens
 */
export async function purchaseLp(
  solAmount: number,
  publicKey: PublicKey,
  connection: Connection,
  sendTransaction: (
    transaction: Transaction,
    connection: Connection,
    options?: { skipPreflight?: boolean }
  ) => Promise<string>
): Promise<{
  success: boolean
  txHash?: string
  tokens?: number
  error?: string
}> {
  // Check if presale is still active
  if (!isPresaleActive()) {
    return {
      success: false,
      error: 'Presale period has ended',
    }
  }

  // Validate min purchase
  if (solAmount < PRESALE_CONFIG.minPurchase) {
    return {
      success: false,
      error: `Minimum purchase is ${PRESALE_CONFIG.minPurchase} SOL`,
    }
  }

  // Validate max purchase
  if (solAmount > PRESALE_CONFIG.maxPurchase) {
    return {
      success: false,
      error: `Maximum purchase is ${PRESALE_CONFIG.maxPurchase} SOL`,
    }
  }

  // Check hard cap - get balance from purchase wallet
  try {
    const purchaseWalletPubkey = new PublicKey(
      PRESALE_CONFIG.purchaseWalletAddress
    )
    const purchaseWalletBalance = await connection.getBalance(
      purchaseWalletPubkey
    )
    const currentPurchases = purchaseWalletBalance / LAMPORTS_PER_SOL
    const hardCap = PRESALE_CONFIG.getHardCap()

    if (currentPurchases + solAmount > hardCap) {
      return {
        success: false,
        error: `Purchase would exceed hard cap of ${hardCap} SOL. Current purchases: ${currentPurchases.toFixed(
          2
        )} SOL`,
      }
    }
  } catch (error) {
    console.error('Error checking hard cap:', error)
    // Continue with transaction if we can't check hard cap
  }

  try {
    // Create transaction
    const transaction = new Transaction()

    // Add transfer instruction
    const transferInstruction = SystemProgram.transfer({
      fromPubkey: publicKey,
      toPubkey: new PublicKey(PRESALE_CONFIG.purchaseWalletAddress),
      lamports: Math.floor(solAmount * LAMPORTS_PER_SOL),
    })

    transaction.add(transferInstruction)

    // Get recent blockhash
    const { blockhash } = await connection.getLatestBlockhash('confirmed')
    transaction.recentBlockhash = blockhash
    transaction.feePayer = publicKey

    // Send transaction
    const signature = await sendTransaction(transaction, connection, {
      skipPreflight: false,
    })

    // Calculate tokens
    const tokens = calculateTokensFromSol(solAmount)

    return {
      success: true,
      txHash: signature,
      tokens,
    }
  } catch (error) {
    console.error('Purchase transaction error:', error)

    // Handle specific error types
    if (error instanceof Error) {
      if (error.message.includes('User rejected')) {
        return {
          success: false,
          error: 'Transaction was rejected',
        }
      }
      if (error.message.includes('insufficient funds')) {
        return {
          success: false,
          error: 'Insufficient SOL balance',
        }
      }
      return {
        success: false,
        error: error.message || 'Transaction failed',
      }
    }

    return {
      success: false,
      error: 'An unexpected error occurred',
    }
  }
}

/**
 * @deprecated Use purchaseLp() instead for real blockchain transactions
 */
export async function mockPurchase(solAmount: number): Promise<{
  success: boolean
  txHash?: string
  tokens?: number
  error?: string
}> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1500))

  if (solAmount < PRESALE_CONFIG.minPurchase) {
    return {
      success: false,
      error: `Minimum purchase is ${PRESALE_CONFIG.minPurchase} SOL`,
    }
  }

  if (solAmount > PRESALE_CONFIG.maxPurchase) {
    return {
      success: false,
      error: `Maximum purchase is ${PRESALE_CONFIG.maxPurchase} SOL`,
    }
  }

  const hardCap = PRESALE_CONFIG.getHardCap()
  if (mockRaised + solAmount > hardCap) {
    return {
      success: false,
      error: 'Purchase would exceed hard cap',
    }
  }

  const tokens = calculateTokensFromSol(solAmount)
  if (
    tokens >
    PRESALE_CONFIG.presaleSupply -
      Math.floor(mockRaised / PRESALE_CONFIG.pricePerToken)
  ) {
    return {
      success: false,
      error: 'Not enough tokens remaining in presale',
    }
  }

  mockRaised += solAmount
  mockParticipants += 1

  // Generate mock transaction hash
  const txHash = `mock_${Math.random()
    .toString(36)
    .substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`

  return {
    success: true,
    txHash,
    tokens,
  }
}

export function isPresaleActive(): boolean {
  const now = new Date()
  const start = PRESALE_CONFIG.getPresaleStartDate()
  const end = PRESALE_CONFIG.getPresaleEndDate()
  return now >= start && now <= end
}

export function getTimeRemaining(): {
  days: number
  hours: number
  minutes: number
  seconds: number
} | null {
  const now = new Date()
  const start = PRESALE_CONFIG.getPresaleStartDate()
  const end = PRESALE_CONFIG.getPresaleEndDate()

  if (now < start) {
    const diff = start.getTime() - now.getTime()
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    }
  }

  if (now > end) {
    return null
  }

  const diff = end.getTime() - now.getTime()
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  }
}

// Airdrop tier information
export const AIRDROP_TIERS = {
  platinum: {
    name: 'Platinum',
    amount: 1640000, // 1,640,000 $PULSE
    color: '#E5E7EB', // Silver/Platinum color
  },
  purple: {
    name: 'Purple',
    amount: 420000, // 420,000 $PULSE
    color: '#A78BFA', // Purple color
  },
  black: {
    name: 'Black',
    amount: 90000, // 90,000 $PULSE
    color: '#1F2937', // Black color
  },
}
